import type { CarInput, CarState } from './types';

/**
 * Paramètres de conduite arcade (modèle hybride « top-down car » d'iforce2d, transposé
 * en espace-vitesse puisque Matter n'arrive qu'en M2). Le cœur du feeling est la
 * FRICTION LATÉRALE : on annule la composante de vitesse perpendiculaire aux roues,
 * dans la limite d'un grip max (`gripAccel`). En dessous → la voiture accroche ; au-delà
 * → elle glisse (drift). Le frein à main effondre temporairement ce grip.
 *
 * Les 5 leviers de feeling (cf. PLAN.md §3) : engineForce, maxSpeed, turnRate,
 * gripAccel, drag.
 */
export interface VehicleParams {
  /** Accélération moteur (px/s²). */
  engineForce: number;
  /** Décélération au frein quand on avance (px/s²). */
  brakeForce: number;
  /** Accélération en marche arrière (px/s²). */
  reverseForce: number;
  /** Vitesse max en marche avant (px/s). */
  maxSpeed: number;
  /** Vitesse max en marche arrière (px/s). */
  reverseMaxSpeed: number;
  /** Traînée linéaire passive (1/s). */
  drag: number;
  /** Autorité de lacet à pleine vitesse (rad/s) pour un véhicule de longueur TURN_REF_LENGTH. */
  turnRate: number;
  /** Vitesse au-dessus de laquelle le braquage est plein (px/s). */
  refSpeed: number;
  /**
   * Vitesse de l'actionneur de direction (1/s) : le braquage rejoint la commande à ce rythme.
   * BAS = roues lentes à tourner (délai commande→réponse, sensation de poids). Curseur « délai ».
   */
  steerResponse: number;
  /** Grip latéral : vitesse latérale max annulable par seconde (px/s²). Le curseur n°1. */
  gripAccel: number;
  /** Grip latéral sous frein à main (px/s²) — bas = ça décroche. */
  handbrakeGripAccel: number;
  /** Vitesse latérale (px/s) au-delà de laquelle grip = 0 (échelle d'affichage du drift). */
  driftReference: number;
  /**
   * Multiplicateur du grip latéral pendant un contact mur [0..1]. Bas = la voiture rebondit
   * au lieu de rester collée : sinon la friction latérale annule la vitesse de rebond que
   * Matter vient de renvoyer. Curseur « anti-scotch au mur ».
   */
  contactGripFactor: number;
}

export const DEFAULT_VEHICLE: VehicleParams = {
  engineForce: 1200,
  brakeForce: 2000,
  reverseForce: 700,
  maxSpeed: 380,
  reverseMaxSpeed: 140,
  drag: 1.0,
  turnRate: 3.4,
  refSpeed: 130,
  steerResponse: 7.0,
  gripAccel: 1100, // assez haut pour coller en conduite normale, assez bas pour décrocher à fond
  handbrakeGripAccel: 320,
  driftReference: 70,
  contactGripFactor: 0.12,
};

/**
 * Longueur (px) à laquelle `turnRate` est calibré. Le lacet est divisé par la longueur réelle
 * du véhicule : un véhicule PLUS LONG tourne d'autant moins (rayon de braquage plus grand) —
 * c'est l'effet de l'empattement, « selon la longueur ». La voiture (28) garde donc son feeling.
 */
const TURN_REF_LENGTH = 28;

/**
 * Calcule le contrôle de conduite pour un pas fixe `dt` (s) : met à jour la VÉLOCITÉ
 * (vx, vy), le cap (angle) et les indicateurs (lateralSlip, grip). N'intègre PAS la
 * position : depuis M2 c'est Matter qui déplace le corps et résout les collisions
 * (le contrôleur reste maison — modèle hybride iforce2d).
 *
 * Ordre (cf. PLAN.md §3) : forces moteur/frein → traînée → clamp vitesse →
 * friction latérale (kill-lateral + clamp = grip/drift) → braquage.
 */
export function integrateCar(
  car: CarState,
  input: CarInput,
  dt: number,
  p: VehicleParams = DEFAULT_VEHICLE,
  /** Vrai si la voiture touche un mur ce pas : on effondre le grip pour laisser le rebond vivre. */
  contact = false,
): void {
  const fx = Math.cos(car.angle);
  const fy = Math.sin(car.angle);
  const rx = -Math.sin(car.angle); // axe « droite » du véhicule
  const ry = Math.cos(car.angle);

  const forwardSpeed = car.vx * fx + car.vy * fy;

  // 1) Forces longitudinales (le long du cap).
  let longAccel = 0;
  if (input.throttle > 0) longAccel += input.throttle * p.engineForce;
  if (input.brake > 0) {
    longAccel -= input.brake * (forwardSpeed > 5 ? p.brakeForce : p.reverseForce);
  }
  car.vx += fx * longAccel * dt;
  car.vy += fy * longAccel * dt;

  // 2) Traînée passive.
  const dragFactor = p.drag * dt;
  car.vx -= car.vx * dragFactor;
  car.vy -= car.vy * dragFactor;

  // 3) Clamp de la vitesse (cap différent en marche arrière).
  let speed = Math.hypot(car.vx, car.vy);
  const forwardAfter = car.vx * fx + car.vy * fy;
  const cap = forwardAfter >= 0 ? p.maxSpeed : p.reverseMaxSpeed;
  if (speed > cap) {
    const k = cap / speed;
    car.vx *= k;
    car.vy *= k;
    speed = cap;
  }

  // 4) FRICTION LATÉRALE — le cœur du feeling.
  //    On annule la glisse perpendiculaire, dans la limite d'un grip max par pas.
  const lateralSpeed = car.vx * rx + car.vy * ry;
  const baseGrip = input.handbrake ? p.handbrakeGripAccel : p.gripAccel;
  // En contact mur, on effondre le grip latéral : sinon il annule la vitesse de rebond
  // renvoyée par Matter et la voiture reste « collée » au mur.
  const gripAccel = contact ? baseGrip * p.contactGripFactor : baseGrip;
  const maxDV = gripAccel * dt;
  const removed = clamp(lateralSpeed, -maxDV, maxDV);
  car.vx -= rx * removed;
  car.vy -= ry * removed;

  const residualSlip = lateralSpeed - removed; // ce qui n'a pas pu être annulé = le drift
  car.lateralSlip = residualSlip;
  car.grip = 1 - Math.min(1, Math.abs(residualSlip) / p.driftReference);

  // 5) Direction. ACTIONNEUR : le braquage réel (car.steerAngle) rejoint la commande à vitesse
  //    limitée (steerResponse) → un délai « le temps de tourner les roues » (reste arcade).
  const cmd = clamp(input.steer, -1, 1);
  const maxDSteer = p.steerResponse * dt;
  car.steerAngle += clamp(cmd - car.steerAngle, -maxDSteer, maxDSteer);
  // Lacet scalé par la vitesse ET par la LONGUEUR (un véhicule long vire plus large). Sous frein
  // à main on prend la vitesse totale (rotation même en glisse). La voiture (w=28) reste à l'identique.
  const speedForTurn = input.handbrake ? speed : forwardSpeed;
  const steerFactor = clamp(speedForTurn / p.refSpeed, -1, 1);
  car.angularVelocity = car.steerAngle * p.turnRate * steerFactor * (TURN_REF_LENGTH / car.w);
  car.angle += car.angularVelocity * dt;

  car.speed = Math.hypot(car.vx, car.vy);
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
